import React, { forwardRef, ImgHTMLAttributes, ReactNode, useState, useEffect } from 'react';

import cn from 'classnames';

import { ImageStatus, NativeImgProps } from '@shared/ui/Image/types';

import cls from './Image.module.css';

interface ImageProps extends Omit<NativeImgProps, 'onLoad' | 'onError'> {
    withLoader?: boolean;
    loader?: ReactNode;
    wrapperClassName?: string;
    onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
    onError?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

export const Image = forwardRef<HTMLImageElement, ImageProps>((props, ref) => {
    const {
        withLoader = true,
        loader,
        wrapperClassName,
        className,
        src,
        alt,
        onLoad,
        onError,
        ...rest
    } = props;

    const [status, setStatus] = useState<ImageStatus>(() => (src ? 'loading' : 'idle'));
    const [currentSrc, setCurrentSrc] = useState(src);

    useEffect(() => {
        setCurrentSrc(src);
        setStatus(src ? 'loading' : 'idle');
    }, [src]);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        setStatus('loaded');
        onLoad?.(e);
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        setStatus('error');
        onError?.(e);
    };

    const showLoader = withLoader && status === 'loading';
    const showFallback = status === 'error';

    const { width } = rest as ImgHTMLAttributes<HTMLImageElement>;
    const { height } = rest as ImgHTMLAttributes<HTMLImageElement>;

    const fallbackStyle: React.CSSProperties = {
        width: typeof width === 'number' ? `${width}px` : width || undefined,
        height: typeof height === 'number' ? `${height}px` : height || undefined,
    };

    return (
        <span className={cn(cls.root, wrapperClassName)}>
            {showLoader && (
                <span className={cls.loader}>
                    {loader ?? <span className={cls.defaultSpinner} aria-hidden="true" />}
                </span>
            )}

            {!showFallback && currentSrc && (
                <img
                    ref={ref}
                    src={currentSrc}
                    alt={alt}
                    className={cn(
                        cls.img,
                        {
                            [cls.imgHidden]: withLoader && status !== 'loaded',
                        },
                        className
                    )}
                    onLoad={handleLoad}
                    onError={handleError}
                    {...rest}
                />
            )}

            {showFallback && (
                <span className={cls.fallback} style={fallbackStyle}>
                    <img
                        src="/images/fallback-icon.png"
                        alt={alt || 'Изображение недоступно'}
                        className={cls.fallbackImg}
                    />
                </span>
            )}
        </span>
    );
});

Image.displayName = 'Image';
